import { useNavigate, useParams } from "react-router-dom";
import { useState, useEffect, useCallback } from "react";

import { Box, Button, Chip, FormControl, Grid2, InputLabel, Link, MenuItem, Select, Stack, TextField, Typography, useTheme } from "@mui/material";
import PageTitle from "../components/PageTitle";
import { api, Match, MatchStatus, Player, VersionChannel } from "../../shared/api/client";
import useGameAndVersion from "../../shared/hooks/useGameAndVersion";
import { InternalLink } from "../../shared/components/InternalLink";
import BoxCard from "../../shared/components/BoxCard";
import { useAuth } from "../../shared/auth/AuthContext";
import { useMatchSubscription } from "../../shared/hooks/useMatchSubscription";

function PlayerInfo({ player }: { player: Player }) {
  return <Box>{player.name}</Box>;
}

export default function MatchSummary() {
  const theme = useTheme();
  const navigate = useNavigate();
  const { matchId } = useParams();
  const { user, isSuperuser } = useAuth();
  const [matchDoc, setMatchDoc] = useState<Match | undefined>(undefined);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!matchId) {
    throw "must have match id";
  }

  useEffect(() => {
    api.getMatch(matchId)
      .then((data) => {
        setMatchDoc(data.match);
        setLoading(false);
      })
      .catch((err) => {
        setError(err.message);
        setLoading(false);
      });
  }, [matchId]);

  // Subscribe to real-time match updates
  const handleMatchUpdate = useCallback((updatedMatch: Match) => {
    setMatchDoc(updatedMatch);
  }, []);

  useMatchSubscription(matchId, handleMatchUpdate);

  const { game, gameVersion } = useGameAndVersion(
    matchDoc?.gameId,
    null,
    matchDoc?.majorVersion,
    matchDoc?.minorVersionChannel
  );

  const isGameOwnerOrAdmin = !!(game && user && (game.ownerUserId === user.id || isSuperuser));
  const [debugMatch, setDebugMatch] = useState<Record<string, unknown> | null>(null);
  const [debugLoading, setDebugLoading] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState<MatchStatus>(matchDoc?.status ?? 'pending');
  const [selectedChannel, setSelectedChannel] = useState<VersionChannel>(matchDoc?.minorVersionChannel ?? 'stable');
  const [editedState, setEditedState] = useState<string>('');
  const [stateEditMode, setStateEditMode] = useState(false);
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    if (!isGameOwnerOrAdmin || !matchId) return;
    setDebugLoading(true);
    api.getMatchDebug(matchId)
      .then((data) => setDebugMatch(data.match as unknown as Record<string, unknown>))
      .catch(() => setDebugMatch(null))
      .finally(() => setDebugLoading(false));
  }, [isGameOwnerOrAdmin, matchId]);

  useEffect(() => {
    if (matchDoc) {
      setSelectedStatus(matchDoc.status);
      setSelectedChannel(matchDoc.minorVersionChannel ?? 'stable');
    }
  }, [matchDoc?.status, matchDoc?.minorVersionChannel]);

  useEffect(() => {
    if (debugMatch?.state && !stateEditMode) {
      setEditedState(JSON.stringify(debugMatch.state, null, 2));
    }
  }, [debugMatch?.state, stateEditMode]);

  const hasControlChanges = selectedStatus !== matchDoc?.status ||
    selectedChannel !== (matchDoc?.minorVersionChannel ?? 'stable');

  const applyMatchChanges = async () => {
    if (!matchDoc) return;

    const updates: Record<string, unknown> = {};
    if (selectedStatus !== matchDoc.status) {
      updates.status = selectedStatus;
    }
    if (selectedChannel !== (matchDoc.minorVersionChannel ?? 'stable')) {
      updates.minorVersionChannel = selectedChannel;
    }
    if (Object.keys(updates).length === 0) return;

    setUpdating(true);
    try {
      const { match: updatedMatch } = await api.updateMatch(matchId, updates);
      setMatchDoc(updatedMatch);
      setSelectedStatus(updatedMatch.status);
      setSelectedChannel(updatedMatch.minorVersionChannel ?? 'stable');
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to update match");
      setSelectedStatus(matchDoc.status);
      setSelectedChannel(matchDoc.minorVersionChannel ?? 'stable');
    } finally {
      setUpdating(false);
    }
  };

  const resetControlChanges = () => {
    if (!matchDoc) return;
    setSelectedStatus(matchDoc.status);
    setSelectedChannel(matchDoc.minorVersionChannel ?? 'stable');
  };

  const saveMatchState = async () => {
    try {
      const parsedState = JSON.parse(editedState);
      if (typeof parsedState !== 'object' || parsedState === null || Array.isArray(parsedState)) {
        setError("State must be a JSON object, not an array or primitive");
        return;
      }
      setUpdating(true);
      const { match: updatedMatch } = await api.updateMatchStateAdmin(matchId, parsedState);
      setMatchDoc(updatedMatch);
      setDebugMatch({ ...debugMatch, state: parsedState } as Record<string, unknown>);
      setStateEditMode(false);
    } catch (err) {
      if (err instanceof SyntaxError) {
        setError("Invalid JSON: " + err.message);
      } else {
        setError(err instanceof Error ? err.message : "Failed to update match state");
      }
    } finally {
      setUpdating(false);
    }
  };

  const cancelStateEdit = () => {
    setStateEditMode(false);
    if (debugMatch?.state) {
      setEditedState(JSON.stringify(debugMatch.state, null, 2));
    }
  };

  if (loading || !game || !gameVersion || !matchDoc) {
    return <PageTitle title="Loading...." />;
  }

  if (error) {
    return <PageTitle title={`Error: ${error}`} />;
  }

  const currentPlayerCount = Object.keys(matchDoc.players).length;
  const maxPlayerCount = matchDoc.numPlayers;
  const isPlaying = !!matchDoc.players[user?.id ?? "--"];

  const leaveGame = async () => {
    if (!user) {
      throw "must be logged in to leave the game";
    }

    setActionLoading(true);
    try {
      const { match: updatedMatch } = await api.leaveMatch(matchId);
      if (Object.keys(updatedMatch.players).length === 0) {
        navigate(`/game/${matchDoc.gameId}`);
      } else {
        setMatchDoc(updatedMatch);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to leave match");
    } finally {
      setActionLoading(false);
    }
  };

  const joinGame = async () => {
    if (!user) {
      throw "must be logged in to join the game";
    }

    setActionLoading(true);
    try {
      const { match: updatedMatch } = await api.joinMatch(matchId);
      setMatchDoc(updatedMatch);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to join match");
    } finally {
      setActionLoading(false);
    }
  };

  const statusColors: Record<string, "default" | "primary" | "success" | "warning" | "error"> = {
    pending: "warning",
    active: "primary",
    preparing: "primary",
    finished: "success",
    cancelled: "error",
    abandoned: "error",
  };

  const isDiscordMatch = !!matchDoc.discordThreadId;
  const canJoin = matchDoc.status === "pending" && !isPlaying && currentPlayerCount < maxPlayerCount && !isDiscordMatch;
  const canLeave = matchDoc.status === "pending" && isPlaying;

  return (
    <Box>
      <PageTitle title={matchDoc.name} />

      <Grid2 container spacing={2}>
        {/* Game Info Card */}
        <Grid2 size={{ xs: 12, md: 4 }}>
          <BoxCard title={game.name}>
            {game.boxArtUrl && (
              <Box
                component="img"
                src={game.boxArtUrl}
                alt={game.name}
                sx={{
                  width: "100%",
                  maxWidth: 200,
                  borderRadius: 1,
                  mb: 2,
                }}
              />
            )}
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              <InternalLink to={`/game/${game.id}`}>View Game Page</InternalLink>
            </Typography>
          </BoxCard>
        </Grid2>

        {/* Match Info Card */}
        <Grid2 size={{ xs: 12, md: 4 }}>
          <BoxCard title="Match Info">
            <Stack spacing={1}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography variant="body2" color="text.secondary">Status:</Typography>
                <Chip
                  label={matchDoc.status}
                  size="small"
                  color={statusColors[matchDoc.status] || "default"}
                />
              </Box>
              <Typography variant="body2" color="text.secondary">
                Created by: {matchDoc.createdByName}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Created: {new Date(matchDoc.createdAt).toLocaleDateString()}{" "}
                {new Date(matchDoc.createdAt).toLocaleTimeString()}
              </Typography>
              {matchDoc.options && gameVersion.configurationOptions?.map((opt) => {
                const selectedId = matchDoc.options?.[opt.fieldName];
                if (!selectedId) return null;
                const choiceLabel = opt.config.choices.find(c => c.id === selectedId)?.label ?? selectedId;
                return (
                  <Typography key={opt.fieldName} variant="body2" color="text.secondary">
                    {opt.config.label}: {choiceLabel}
                  </Typography>
                );
              })}
              {matchDoc.status === "active" && (
                <Button
                  variant="contained"
                  size="small"
                  component={Link}
                  href={`/player/${matchDoc.id}`}
                  sx={{
                    background: `linear-gradient(135deg, ${theme.palette.info.main} 0%, ${theme.palette.primary.main} 100%)`,
                    fontWeight: 600,
                    mt: 1,
                    alignSelf: "flex-start",
                  }}
                >
                  Launch Player
                </Button>
              )}
              {matchDoc.discordThreadId && matchDoc.discordGuildId && (
                <Button
                  variant="outlined"
                  size="small"
                  component={Link}
                  href={`https://discord.com/channels/${matchDoc.discordGuildId}/${matchDoc.discordThreadId}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    borderColor: theme.palette.primary.main,
                    color: theme.palette.text.primary,
                    mt: 1,
                    alignSelf: "flex-start",
                    "&:hover": {
                      borderColor: theme.palette.primary.light,
                      background: "rgba(255, 63, 127, 0.1)",
                    },
                  }}
                >
                  Discord Thread
                </Button>
              )}
            </Stack>
          </BoxCard>
        </Grid2>

        {/* Players Card */}
        <Grid2 size={{ xs: 12, md: 4 }}>
          <BoxCard title={`Players (${currentPlayerCount}/${maxPlayerCount})`}>
            <Stack spacing={1}>
              {Object.keys(matchDoc.players).map((playerId) => (
                <PlayerInfo key={playerId} player={matchDoc.players[playerId]} />
              ))}
              {currentPlayerCount === 0 && (
                <Typography variant="body2" color="text.secondary">
                  No players yet
                </Typography>
              )}
              {canJoin && (
                <Button
                  variant="contained"
                  size="small"
                  disabled={actionLoading}
                  onClick={() => {
                    if (!user) {
                      window.localStorage.setItem("postLoginUrl", document.location.href);
                      navigate("/login");
                      return;
                    }
                    joinGame();
                  }}
                  sx={{
                    background: `linear-gradient(135deg, ${theme.palette.info.main} 0%, ${theme.palette.primary.main} 100%)`,
                    fontWeight: 600,
                    mt: 1,
                    alignSelf: "flex-start",
                  }}
                >
                  {actionLoading ? "Joining..." : "Join"}
                </Button>
              )}
              {isDiscordMatch && matchDoc.status === "pending" && !isPlaying && currentPlayerCount < maxPlayerCount && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  This match was created via Discord.{" "}
                  {matchDoc.discordGuildId ? (
                    <Link
                      href={`https://discord.com/channels/${matchDoc.discordGuildId}/${matchDoc.discordThreadId}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Join in Discord
                    </Link>
                  ) : (
                    "Join via the Discord thread."
                  )}
                </Typography>
              )}
              {canLeave && (
                <Button
                  variant="outlined"
                  size="small"
                  disabled={actionLoading}
                  onClick={leaveGame}
                  sx={{
                    borderColor: theme.palette.primary.main,
                    color: theme.palette.text.primary,
                    mt: 1,
                    alignSelf: "flex-start",
                    "&:hover": {
                      borderColor: theme.palette.primary.light,
                      background: "rgba(255, 63, 127, 0.1)",
                    },
                  }}
                >
                  {actionLoading ? "Leaving..." : "Leave"}
                </Button>
              )}
            </Stack>
          </BoxCard>
        </Grid2>
      </Grid2>

      {isGameOwnerOrAdmin && (
        <BoxCard title="Match Controls (Admin)" sx={{ mt: 2 }}>
          <Stack spacing={2}>
            {/* Status Control */}
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Match status:
              </Typography>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={selectedStatus}
                  label="Status"
                  disabled={updating}
                  onChange={(e) => setSelectedStatus(e.target.value as MatchStatus)}
                >
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="preparing">Preparing</MenuItem>
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="finished">Finished</MenuItem>
                  <MenuItem value="cancelled">Cancelled</MenuItem>
                  <MenuItem value="abandoned">Abandoned</MenuItem>
                </Select>
              </FormControl>
            </Box>

            {/* Version Channel Control */}
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Minor version channel:
              </Typography>
              <FormControl fullWidth size="small">
                <InputLabel>Channel</InputLabel>
                <Select
                  value={selectedChannel}
                  label="Channel"
                  disabled={updating}
                  onChange={(e) => setSelectedChannel(e.target.value as VersionChannel)}
                >
                  <MenuItem value="stable">Stable</MenuItem>
                  <MenuItem value="beta">Beta</MenuItem>
                </Select>
              </FormControl>
            </Box>

            {hasControlChanges && (
              <Stack direction="row" spacing={1}>
                <Button
                  size="small"
                  variant="contained"
                  disabled={updating}
                  onClick={applyMatchChanges}
                >
                  {updating ? "Applying..." : "Apply Changes"}
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  disabled={updating}
                  onClick={resetControlChanges}
                >
                  Reset
                </Button>
              </Stack>
            )}

            {/* State Editor */}
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Edit match state (JSON):
              </Typography>
              <TextField
                multiline
                fullWidth
                value={editedState}
                onChange={(e) => setEditedState(e.target.value)}
                slotProps={{
                  input: {
                    readOnly: !stateEditMode,
                    sx: { fontFamily: "monospace", fontSize: 12 }
                  }
                }}
                minRows={6}
                maxRows={20}
                error={stateEditMode && (() => {
                  try { JSON.parse(editedState); return false; } catch { return true; }
                })()}
                helperText={stateEditMode && (() => {
                  try { JSON.parse(editedState); return "Valid JSON"; } catch (e) { return `Invalid JSON: ${(e as Error).message}`; }
                })()}
              />
              <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                {!stateEditMode ? (
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => setStateEditMode(true)}
                    disabled={!debugMatch?.state}
                  >
                    Edit State
                  </Button>
                ) : (
                  <>
                    <Button
                      size="small"
                      variant="contained"
                      onClick={saveMatchState}
                      disabled={updating || (() => { try { JSON.parse(editedState); return false; } catch { return true; } })()}
                    >
                      Save State
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={cancelStateEdit}
                      disabled={updating}
                    >
                      Cancel
                    </Button>
                  </>
                )}
              </Stack>
            </Box>
          </Stack>
        </BoxCard>
      )}

      {isGameOwnerOrAdmin && (
        <Stack spacing={2} sx={{ mt: 2 }}>
          <BoxCard title="Match Metadata (Debug)">
            {debugLoading ? (
              <Typography variant="body2" color="text.secondary">Loading...</Typography>
            ) : debugMatch ? (
              <TextField
                multiline
                fullWidth
                value={JSON.stringify(Object.fromEntries(Object.entries(debugMatch).filter(([k]) => k !== "state")), null, 2)}
                slotProps={{ input: { readOnly: true, sx: { fontFamily: "monospace", fontSize: 12 } } }}
                minRows={6}
                maxRows={20}
              />
            ) : (
              <Typography variant="body2" color="text.secondary">Unable to load match data.</Typography>
            )}
          </BoxCard>
        </Stack>
      )}
    </Box>
  );
}
